export type Visualization = "auto" | "chart" | "kpi";
export type TelemetryRange = "1h" | "24h" | "7d";
export type TelemetryPreferences = {
  fieldId: string;
  mode: Visualization;
  range: TelemetryRange;
};

const storagePrefix = "allsetra-telemetry-view:v1:";

const defaults: TelemetryPreferences = {
  fieldId: "",
  mode: "auto",
  range: "1h"
};

export function telemetryPreferenceKey(
  userId: string,
  tenantId: string,
  accountId: string,
  objectId: string
): string | null {
  return userId
    ? `${storagePrefix}${JSON.stringify([userId, tenantId, accountId, objectId])}`
    : null;
}

export function loadTelemetryPreferences(
  key: string | null
): TelemetryPreferences {
  try {
    const stored = key ? JSON.parse(localStorage.getItem(key) ?? "null") : null;
    return {
      fieldId: typeof stored?.fieldId === "string" ? stored.fieldId : "",
      mode:
        stored?.mode === "chart" || stored?.mode === "kpi"
          ? stored.mode
          : "auto",
      range:
        stored?.range === "24h" || stored?.range === "7d" ? stored.range : "1h"
    };
  } catch {
    return { ...defaults };
  }
}

export function saveTelemetryPreferences(
  key: string | null,
  preferences: TelemetryPreferences
) {
  try {
    if (key) localStorage.setItem(key, JSON.stringify(preferences));
  } catch {
    // Preferences are optional when browser storage is blocked or full.
  }
}

/** Snapshot only non-sensitive view settings before logout clears authentication storage. */
export function preserveTelemetryPreferencesOnLogout(): () => void {
  const saved = new Map<string, TelemetryPreferences>();
  try {
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(storagePrefix)) {
        saved.set(key, loadTelemetryPreferences(key));
      }
    }
  } catch {
    // Optional preference storage must never prevent logout.
  }
  return () =>
    saved.forEach((preferences, key) =>
      saveTelemetryPreferences(key, preferences)
    );
}
